import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Check,
  CheckCircle,
  Info,
  ShieldAlert,
  X,
  XCircle,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import apiService, { extractAffiliateFundsList } from '../utils/apiService';
import SubPaymentMethodGrid from '../components/withdrawal/SubPaymentMethodGrid';
import WalletMethodIcon from '../components/withdrawal/WalletMethodIcon';
import {
  extractWalletsList,
  getConvertedAmount,
  getWalletLimits,
  getWithdrawalRate,
  isBankWallet,
  isLocalBankWallet,
  mapWalletToMethod,
  walletNeedsSubPaymentMethods,
} from '../utils/withdrawalWallets';
import {
  resolveSubPaymentMethods,
  subMethodNeedsAddress,
} from '../utils/subPaymentMethods';
import { getThemeColor } from '../config/appConfig';
import {
  extractPartnerPaymentDetailsList,
  mapPartnerPaymentDetail,
  maskAccountNumber,
  accountMatchesNetwork,
  shortenAddress,
} from '../utils/partnerPaymentDetails';
import { useFetchOnceByKey } from '../hooks/useMountOnce';
import { useKycVerified } from '../hooks/useKycVerified';

const THEME = getThemeColor('primary');

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const StatusBadge = ({ status }) => {
  const s = (status || '').toString().toLowerCase();
  if (['approved', 'completed', 'success'].includes(s)) {
    return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Completed</span>;
  }
  if (['pending', 'processing', 'in_progress'].includes(s)) {
    return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Pending</span>;
  }
  return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{status || '—'}</span>;
};

const Withdrawal = () => {
  const { isDarkMode } = useTheme();
  const { kycVerified, loading: kycLoading } = useKycVerified();
  const [wallets, setWallets] = useState([]);
  const [isLoadingWallets, setIsLoadingWallets] = useState(true);
  const [withdrawalData, setWithdrawalData] = useState({
    amount: '',
    selectedWalletId: '',
    selectedSubPaymentMethodId: '',
    cryptoAddress: '',
  });
  const [subPaymentMethods, setSubPaymentMethods] = useState([]);
  const [isLoadingSubMethods, setIsLoadingSubMethods] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [transactionWidgetData, setTransactionWidgetData] = useState(null);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState(true);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [selectedSavedAccountId, setSelectedSavedAccountId] = useState('');
  const [isLoadingSavedAccounts, setIsLoadingSavedAccounts] = useState(true);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const availableBalance = transactionWidgetData?.availableBalance ?? 0;

  const selectedWallet = wallets.find((w) => w.id === withdrawalData.selectedWalletId);
  const selectedSubPaymentMethod = subPaymentMethods.find(
    (m) => m.id === withdrawalData.selectedSubPaymentMethodId
  );
  const hasSubPaymentMethods = subPaymentMethods.length > 0;
  const isBankMethod = isBankWallet(selectedWallet) && !hasSubPaymentMethods;
  const isLocalBank = isLocalBankWallet(selectedWallet);
  const needsCryptoAddress = hasSubPaymentMethods
    ? subMethodNeedsAddress(selectedSubPaymentMethod)
    : !isBankWallet(selectedWallet);
  const walletLimits = getWalletLimits(selectedWallet, availableBalance);
  const displayVal = (val) => (val?.toString().trim() ? val : '—');
  const amountNum = parseFloat(withdrawalData.amount) || 0;
  const withdrawalRate = getWithdrawalRate(selectedWallet);
  const receiveAmount = getConvertedAmount(amountNum, selectedWallet);

  const matchingSavedAccounts = useMemo(() => {
    if (!selectedWallet?.walletId) return [];
    return savedAccounts.filter((a) => String(a.walletId) === String(selectedWallet.walletId));
  }, [savedAccounts, selectedWallet?.walletId]);

  const networkMatchedAccounts = useMemo(() => {
    if (!hasSubPaymentMethods || !selectedSubPaymentMethod?.value) return [];
    return matchingSavedAccounts.filter((account) =>
      accountMatchesNetwork(account, selectedSubPaymentMethod.value)
    );
  }, [matchingSavedAccounts, hasSubPaymentMethods, selectedSubPaymentMethod?.value]);

  const activeSavedAccounts = hasSubPaymentMethods ? networkMatchedAccounts : matchingSavedAccounts;

  const selectedSavedAccount =
    activeSavedAccounts.find((a) => a.id === selectedSavedAccountId) || activeSavedAccounts[0] || null;

  const paymentDetailsError = useMemo(() => {
    if (!selectedWallet || isLoadingSavedAccounts) return '';

    if (hasSubPaymentMethods) {
      if (!selectedSubPaymentMethod) {
        return 'Please select a payment network to continue.';
      }
      if (networkMatchedAccounts.length === 0) {
        return 'Withdrawal details not found for this network. Please add wallet details in Settings.';
      }
      if (needsCryptoAddress && !selectedSavedAccount?.cryptoAddress?.trim()) {
        return 'TRC20 wallet address missing in withdrawal details. Please add wallet details in Settings.';
      }
      return '';
    }

    if (matchingSavedAccounts.length === 0) {
      return 'Withdrawal details not found for this method. Please add wallet details in Settings.';
    }

    if (needsCryptoAddress && !selectedSavedAccount?.cryptoAddress?.trim()) {
      return 'TRC20 wallet address missing in withdrawal details. Please add wallet details in Settings.';
    }

    return '';
  }, [
    selectedWallet,
    isLoadingSavedAccounts,
    hasSubPaymentMethods,
    selectedSubPaymentMethod,
    networkMatchedAccounts.length,
    matchingSavedAccounts.length,
    needsCryptoAddress,
    selectedSavedAccount,
  ]);

  const canProceedWithdrawal = Boolean(
    selectedWallet &&
    !isLoadingSavedAccounts &&
    !paymentDetailsError &&
    selectedSavedAccount
  );

  const mapFundsToHistory = (funds) =>
    funds.map((fund) => ({
      id: fund.affiliateFundId,
      amount: fund.amount,
      method: fund.accountBank || 'Bank Transfer',
      status: fund.status,
      date: fund.createdDateTime,
    }));

  const loadWithdrawalHistory = useCallback(async ({ cancelled }) => {
    setIsLoadingHistory(true);
    try {
      const response = await apiService.getAffiliateFunds(
        { pageNumber: 1, pageSize: 100, searchTerm: '' }
      );
      if (cancelled()) return;
      if (response.success && response.data?.data) {
        setWithdrawalHistory(mapFundsToHistory(extractAffiliateFundsList(response)));
      }
    } catch {
      if (!cancelled()) setWithdrawalHistory([]);
    } finally {
      if (!cancelled()) setIsLoadingHistory(false);
    }
  }, []);

  const loadSavedAccounts = useCallback(async ({ cancelled }) => {
    setIsLoadingSavedAccounts(true);
    try {
      const response = await apiService.getPartnerPaymentDetails();
      if (cancelled()) return;
      if (response?.success) {
        setSavedAccounts(
          extractPartnerPaymentDetailsList(response).map(mapPartnerPaymentDetail)
        );
      } else {
        setSavedAccounts([]);
      }
    } catch {
      if (!cancelled()) setSavedAccounts([]);
    } finally {
      if (!cancelled()) setIsLoadingSavedAccounts(false);
    }
  }, []);

  const fetchWidget = useCallback(async ({ cancelled }) => {
    setIsLoadingWidgets(true);
    try {
      const response = await apiService.getTransactionWidget();
      if (cancelled()) return;
      if (response.success && response.data) setTransactionWidgetData(response.data);
    } catch {
      if (!cancelled()) setTransactionWidgetData(null);
    } finally {
      if (!cancelled()) setIsLoadingWidgets(false);
    }
  }, []);

  const loadWallets = useCallback(async ({ cancelled }) => {
    setIsLoadingWallets(true);
    try {
      const response = await apiService.getWallets();
      if (cancelled()) return;
      const list = extractWalletsList(response).map(mapWalletToMethod);
      setWallets(list);
      if (list.length > 0) {
        const first = list.find((w) => !w.disabled) || list[0];
        setWithdrawalData((prev) => ({
          ...prev,
          selectedWalletId: prev.selectedWalletId || first.id,
          cryptoAddress: isBankWallet(first) ? prev.cryptoAddress : first.cryptoAddress || '',
        }));
      }
    } catch {
      if (!cancelled()) setWallets([]);
    } finally {
      if (!cancelled()) setIsLoadingWallets(false);
    }
  }, []);

  useFetchOnceByKey(`withdrawal-saved-accounts`, loadSavedAccounts, true);
  useFetchOnceByKey(`withdrawal-history|${historyRefresh}`, loadWithdrawalHistory, true);
  useFetchOnceByKey('withdrawal-widget', fetchWidget, true);
  useFetchOnceByKey('withdrawal-wallets', loadWallets, true);

  useEffect(() => {
    if (hasSubPaymentMethods) {
      if (!selectedSubPaymentMethod) {
        setSelectedSavedAccountId('');
        return;
      }
      if (networkMatchedAccounts.length === 0) {
        setSelectedSavedAccountId('');
        setWithdrawalData((prev) => ({ ...prev, cryptoAddress: '' }));
        return;
      }
      const current = networkMatchedAccounts.find((a) => a.id === selectedSavedAccountId);
      const account = current || networkMatchedAccounts[0];
      if (account.id !== selectedSavedAccountId) {
        setSelectedSavedAccountId(account.id);
      }
      setWithdrawalData((prev) => ({
        ...prev,
        cryptoAddress: account.cryptoAddress || prev.cryptoAddress,
      }));
      return;
    }

    if (matchingSavedAccounts.length === 0) {
      setSelectedSavedAccountId('');
      return;
    }
    const current = matchingSavedAccounts.find((a) => a.id === selectedSavedAccountId);
    const account = current || matchingSavedAccounts[0];
    if (account.id !== selectedSavedAccountId) {
      setSelectedSavedAccountId(account.id);
    }
    if (!isBankWallet(selectedWallet)) {
      setWithdrawalData((prev) => ({
        ...prev,
        cryptoAddress: account.cryptoAddress || prev.cryptoAddress,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync saved account when wallet/network list changes
  }, [
    matchingSavedAccounts,
    networkMatchedAccounts,
    hasSubPaymentMethods,
    selectedSubPaymentMethod?.id,
    selectedWallet?.id,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadSubPaymentMethods = async () => {
      if (!selectedWallet?.walletId) {
        setSubPaymentMethods([]);
        return;
      }

      const embedded = selectedWallet.subPaymentMethods || [];
      if (embedded.length > 0) {
        setSubPaymentMethods(embedded);
        setWithdrawalData((prev) => ({
          ...prev,
          selectedSubPaymentMethodId: '',
        }));
        return;
      }

      const shouldFetchSubMethods = walletNeedsSubPaymentMethods(selectedWallet);
      if (!shouldFetchSubMethods) {
        setSubPaymentMethods([]);
        setWithdrawalData((prev) => ({ ...prev, selectedSubPaymentMethodId: '' }));
        return;
      }

      setIsLoadingSubMethods(true);
      try {
        const response = await apiService.getSubPaymentMethods(selectedWallet.walletId);
        if (cancelled) return;
        const list = resolveSubPaymentMethods(response);
        setSubPaymentMethods(list);
        setWithdrawalData((prev) => ({
          ...prev,
          selectedSubPaymentMethodId: '',
        }));
      } catch {
        if (!cancelled) {
          const list = resolveSubPaymentMethods({ success: false });
          setSubPaymentMethods(list);
          setWithdrawalData((prev) => ({
            ...prev,
            selectedSubPaymentMethodId: '',
          }));
        }
      } finally {
        if (!cancelled) setIsLoadingSubMethods(false);
      }
    };

    loadSubPaymentMethods();
    return () => {
      cancelled = true;
    };
  }, [selectedWallet?.id, selectedWallet?.walletId]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    if (type === 'success') {
      setTimeout(() => clearToast(), 5000);
    }
  };

  const clearToast = () => {
    setToastMessage('');
    setToastType('success');
  };

  const getApiErrorMessage = (response, fallback) => {
    const apiMessage = (response?.message || response?.error || '').toString().trim();
    if (!apiMessage || /^success$/i.test(apiMessage) || /successfully/i.test(apiMessage)) {
      return fallback;
    }
    return apiMessage;
  };

  const setAmountPercent = (pct) => {
    const { max } = walletLimits;
    const val = pct === 'max' ? max : (max * pct) / 100;
    setWithdrawalData((prev) => ({ ...prev, amount: val > 0 ? val.toFixed(2) : '' }));
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setWithdrawalData((prev) => ({ ...prev, amount: '' }));
      return;
    }
    const num = parseFloat(value) || 0;
    if (num > walletLimits.max) return;
    setWithdrawalData((prev) => ({ ...prev, amount: value }));
  };

  const selectWallet = (wallet) => {
    if (!wallet || wallet.disabled) return;
    clearToast();
    setSelectedSavedAccountId('');
    setWithdrawalData((prev) => ({
      ...prev,
      selectedWalletId: wallet.id,
      selectedSubPaymentMethodId: '',
      cryptoAddress: isBankWallet(wallet) ? prev.cryptoAddress : wallet.cryptoAddress || '',
    }));
  };

  const selectSubPaymentMethod = (method) => {
    if (!method || method.disabled) return;
    clearToast();
    setSelectedSavedAccountId('');
    setWithdrawalData((prev) => ({
      ...prev,
      selectedSubPaymentMethodId: method.id,
      cryptoAddress: '',
    }));
  };

  const buildApiPayload = () => {
    const amount = parseFloat(withdrawalData.amount);
    const raw = selectedWallet?.raw || {};
    const walletId = selectedWallet?.walletId ?? raw.walletId ?? null;
    const subPaymentMethodId =
      selectedSubPaymentMethod?.subPaymentMethodId ?? selectedSubPaymentMethod?.id ?? null;
    if (isBankMethod) {
      return {
        amount,
        walletId,
        accountType: 'Bank',
        accountTitle: selectedSavedAccount?.accountTitle || selectedWallet?.accountHolder || raw.accountTitle || '',
        accountBank: selectedSavedAccount?.bankName || selectedWallet?.bankName || raw.accountBank || selectedWallet?.title || '',
        accountNo: selectedSavedAccount?.accountNumber || selectedWallet?.accountNumber || raw.accountNo || '',
        accountIBAN: selectedSavedAccount?.iban || selectedSavedAccount?.swiftCode || selectedWallet?.swiftCode || raw.accountIBAN || '',
        remarks: '',
      };
    }

    const network = hasSubPaymentMethods
      ? selectedSubPaymentMethod?.value || selectedSubPaymentMethod?.title || selectedWallet?.title || ''
      : selectedWallet?.title || raw.accountBank || '';

    return {
      amount,
      walletId,
      subPaymentMethodId,
      accountType: 'Crypto',
      accountTitle: selectedSavedAccount?.accountTitle || raw.accountTitle || '',
      accountBank: network,
      accountNo:
        withdrawalData.cryptoAddress ||
        selectedSavedAccount?.cryptoAddress ||
        selectedWallet?.cryptoAddress ||
        raw.accountNo ||
        '',
      accountIBAN: '',
      remarks: '',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!kycVerified) {
      showToast('Please verify your account before requesting a withdrawal.', 'error');
      return;
    }
    const { min, max } = walletLimits;
    if (!amountNum || (min > 0 && amountNum < min)) {
      showToast(`Minimum withdrawal is ${formatCurrency(min)}`, 'error');
      return;
    }
    if (max > 0 && amountNum > max) {
      showToast(`Maximum withdrawal is ${formatCurrency(max)}`, 'error');
      return;
    }
    if (amountNum > availableBalance) {
      showToast('Amount exceeds available balance', 'error');
      return;
    }
    if (!selectedWallet) {
      showToast('Please select a withdrawal method', 'error');
      return;
    }
    if (!canProceedWithdrawal) {
      showToast(
        paymentDetailsError || 'Please add withdrawal details in Settings before continuing.',
        'error'
      );
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService.createWithdrawalRequest(buildApiPayload());
      if (response?.success) {
        setWithdrawalData((prev) => ({
          ...prev,
          amount: '',
          cryptoAddress: '',
          selectedSubPaymentMethodId: subPaymentMethods.find((m) => !m.disabled)?.id || '',
        }));
        showToast('Withdrawal request submitted successfully!', 'success');
        apiService.clearAffiliateFundsCache();
        const widgetResponse = await apiService.getTransactionWidget();
        if (widgetResponse?.success) setTransactionWidgetData(widgetResponse.data);
        setHistoryRefresh((n) => n + 1);
      } else {
        showToast(getApiErrorMessage(response, 'Withdrawal failed'), 'error');
      }
    } catch {
      showToast('Withdrawal request failed. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const card = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;

  return (
    <div className={`min-h-full ${isDarkMode ? 'bg-black' : 'bg-[#F4F5F7]'}`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-2xl font-bold ${text}`}>Withdraw Funds</h1>
          <p className={`text-sm mt-1 ${muted}`}>
            <Link to="/" className="hover:text-red-800">Dashboard</Link>
            <span className="mx-1.5">›</span>
            <span>Withdrawal</span>
          </p>
        </div>

        {kycLoading ? (
          <div className="flex justify-center py-20">
            <NewtonsCradleLoader />
          </div>
        ) : !kycVerified ? (
          <div
            className={`rounded-2xl border p-6 sm:p-8 shadow-sm ${card}`}
            role="alert"
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                  isDarkMode ? 'bg-amber-950 text-amber-300' : 'bg-amber-50 text-amber-700'
                }`}
              >
                <ShieldAlert className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg font-semibold ${text}`}>Account verification required</h2>
                <p className={`text-sm mt-2 ${muted}`}>
                  Your account is not verified yet. Complete KYC verification to unlock withdrawals.
                </p>
                <Link
                  to="/kyc"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
                  style={{ backgroundColor: THEME }}
                >
                  <ShieldAlert className="w-4 h-4" />
                  Go to Account Verification
                </Link>
              </div>
            </div>
          </div>
        ) : (
        <>
        {toastMessage && toastType === 'error' && (
          <div
            className={`flex items-center gap-2 text-sm rounded-xl px-4 py-3 mb-6 ${
              isDarkMode ? 'bg-red-950 text-red-300' : 'bg-red-50 text-red-700'
            }`}
          >
            <XCircle className="w-4 h-4 shrink-0" />
            <p className="flex-1">{toastMessage}</p>
            <button
              type="button"
              onClick={clearToast}
              className={`shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
              aria-label="Dismiss message"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {toastMessage && toastType === 'success' && (
          <div
            className={`flex items-center gap-2 text-sm rounded-xl px-4 py-3 mb-6 ${
              isDarkMode ? 'bg-emerald-950 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            <CheckCircle className="w-4 h-4 shrink-0" />
            <p className="flex-1">{toastMessage}</p>
            <button
              type="button"
              onClick={clearToast}
              className={`shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
              aria-label="Dismiss message"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">
          {/* Main form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Step 1 */}
            <div className={`rounded-2xl border p-5 sm:p-6 shadow-sm ${card}`}>
              <h2 className={`text-sm font-semibold mb-4 ${text}`}>Step 1: Select Withdrawal Method</h2>
              {isLoadingWallets ? (
                <div className="flex justify-center py-10">
                  <NewtonsCradleLoader />
                </div>
              ) : wallets.length === 0 ? (
                <div className={`text-center py-8 ${muted}`}>
                  <p className="text-sm">No withdrawal methods found.</p>
                  <Link to="/settings/withdrawal-details" className="inline-block mt-2 text-sm font-medium text-red-800 hover:underline">
                    Add Withdrawal Details
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {wallets.map((wallet) => {
                    const selected = withdrawalData.selectedWalletId === wallet.id;
                    return (
                      <button
                        key={wallet.id}
                        type="button"
                        disabled={wallet.disabled}
                        onClick={() => selectWallet(wallet)}
                        className={`relative text-left p-4 rounded-xl border-2 transition ${
                          wallet.disabled
                            ? 'opacity-50 cursor-not-allowed border-gray-100 bg-gray-50'
                            : selected
                              ? 'border-red-800 bg-red-50/50'
                              : isDarkMode
                                ? 'border-gray-700 hover:border-red-800'
                                : 'border-gray-100 hover:border-red-200'
                        }`}
                      >
                        {selected && (
                          <span className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: THEME }}>
                            <Check className="w-3 h-3 text-white" />
                          </span>
                        )}
                        <WalletMethodIcon
                          iconUrl={wallet.iconUrl}
                          methodKey={wallet.methodKey}
                          imgClassName="w-8 h-8 mb-2 object-contain"
                          iconClassName={`w-6 h-6 mb-2 ${selected ? 'text-red-800' : muted}`}
                        />
                        <p className={`text-sm font-semibold ${text}`}>{wallet.title}</p>
                        {wallet.subtitle && (
                          <p className={`text-[11px] mt-0.5 ${muted}`}>{wallet.subtitle}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedWallet && walletNeedsSubPaymentMethods(selectedWallet) && (hasSubPaymentMethods || isLoadingSubMethods) ? (
                <SubPaymentMethodGrid
                  methods={subPaymentMethods}
                  selectedId={withdrawalData.selectedSubPaymentMethodId}
                  onSelect={selectSubPaymentMethod}
                  isLoading={isLoadingSubMethods}
                  isDarkMode={isDarkMode}
                  title="Select Payment Network"
                />
              ) : null}
            </div>

            {/* Step 2 */}
            <div className={`rounded-2xl border p-5 sm:p-6 shadow-sm ${card}`}>
              <h2 className={`text-sm font-semibold mb-4 ${text}`}>Step 2: Withdrawal Details</h2>

              {paymentDetailsError ? (
                <div
                  className={`flex items-start gap-2 text-sm rounded-xl px-4 py-3 mb-5 ${
                    isDarkMode ? 'bg-red-950 text-red-300' : 'bg-red-50 text-red-700'
                  }`}
                >
                  <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p>{paymentDetailsError}</p>
                    <Link
                      to="/settings/withdrawal-details"
                      className="inline-block mt-2 text-xs font-semibold text-red-800 hover:underline"
                    >
                      + Add Wallet Details
                    </Link>
                  </div>
                </div>
              ) : null}

              <div className={`${!canProceedWithdrawal ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm mb-5">
                <span className={muted}>
                  Available Balance:{' '}
                  <strong className={text}>
                    {isLoadingWidgets ? '...' : formatCurrency(availableBalance)}
                  </strong>
                </span>
                {selectedWallet && walletLimits.min > 0 ? (
                  <span className={muted}>
                    Minimum: <strong className={text}>{formatCurrency(walletLimits.min)}</strong>
                  </span>
                ) : null}
                {selectedWallet && Number.isFinite(walletLimits.max) && walletLimits.max < Infinity ? (
                  <span className={muted}>
                    Maximum: <strong className={text}>{formatCurrency(walletLimits.max)}</strong>
                  </span>
                ) : null}
                {isLocalBank ? (
                  <span className={muted}>
                    Conversion Rate: <strong className={text}>{withdrawalRate}</strong>
                  </span>
                ) : null}
              </div>

              <label className={`block text-xs font-medium mb-2 ${muted}`}>Withdrawal Amount</label>
              <div className="flex rounded-xl border overflow-hidden mb-2 focus-within:ring-2 focus-within:ring-red-800/15 focus-within:border-red-800 border-gray-200">
                <input
                  type="number"
                  value={withdrawalData.amount}
                  onChange={handleAmountChange}
                  min={walletLimits.min > 0 ? walletLimits.min : undefined}
                  max={walletLimits.max < Infinity ? walletLimits.max : availableBalance}
                  step="0.01"
                  className={`flex-1 px-4 py-3 text-sm outline-none ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
                  required
                />
                <span className={`flex items-center px-4 text-sm font-medium border-l ${isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                  USD
                </span>
              </div>
              <p className={`text-[11px] mb-3 ${muted}`}>
                {selectedWallet
                  ? `Enter amount between ${formatCurrency(walletLimits.min)} and ${formatCurrency(walletLimits.max)}`
                  : 'Select a withdrawal method first'}
              </p>
              {isLocalBank && amountNum > 0 ? (
                <p className={`text-[11px] mb-3 font-medium ${text}`}>
                  Converted amount: {formatCurrency(receiveAmount)}
                  {` (rate: ${withdrawalRate})`}
                </p>
              ) : null}
              <div className="flex gap-2 mb-6">
                {[25, 50, 75].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setAmountPercent(pct)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                      isDarkMode
                        ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pct}%
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setAmountPercent('max')}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition"
                  style={{ backgroundColor: THEME }}
                >
                  Max
                </button>
              </div>
              </div>

              {hasSubPaymentMethods ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-xs font-medium ${muted}`}>Saved Account Details</label>
                    {selectedSubPaymentMethod ? (
                      <Link to="/settings/withdrawal-details" className="text-xs font-medium text-red-800 hover:underline">
                        + Add Account
                      </Link>
                    ) : null}
                  </div>
                  {!selectedSubPaymentMethod ? (
                    <div className={`rounded-xl border border-dashed p-5 text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-sm font-medium ${text}`}>Payment network selection is required</p>
                      <p className={`text-xs mt-1 ${muted}`}>Please select a payment network above to view saved account details.</p>
                    </div>
                  ) : isLoadingSavedAccounts ? (
                    <p className={`text-xs text-center py-4 ${muted}`}>Loading saved accounts...</p>
                  ) : networkMatchedAccounts.length > 0 && selectedSavedAccount ? (
                    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                      {[
                        { label: 'Account Title', value: selectedSavedAccount.accountTitle },
                        { label: 'Payment Network', value: selectedSubPaymentMethod.title },
                        {
                          label: 'Wallet Address',
                          hint: 'TRC20 address will be used',
                          value: needsCryptoAddress
                            ? selectedSavedAccount.cryptoAddress
                            : selectedSavedAccount.cryptoAddress || '—',
                        },
                      ].map((row) => (
                        <div key={row.label}>
                          <p className={`text-[10px] ${muted}`}>{row.label}</p>
                          {row.hint ? (
                            <p className={`text-[10px] mt-0.5 ${muted}`}>{row.hint}</p>
                          ) : null}
                          <p className={`text-sm font-medium mt-0.5 break-all ${text}`}>
                            {row.label === 'Wallet Address' && row.value && row.value !== '—'
                              ? shortenAddress(row.value)
                              : displayVal(row.value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`rounded-xl border border-dashed p-5 text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-sm font-medium ${text}`}>
                        No saved account for {selectedSubPaymentMethod.title}
                      </p>
                      <p className={`text-xs mt-1 mb-3 ${muted}`}>
                        Add withdrawal details for this network to continue.
                      </p>
                      <Link
                        to="/settings/withdrawal-details"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                        style={{ backgroundColor: THEME }}
                      >
                        + Add Account
                      </Link>
                    </div>
                  )}
                </>
              ) : isBankMethod ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-xs font-medium ${muted}`}>Saved Account Details</label>
                    <Link to="/settings/withdrawal-details" className="text-xs font-medium text-red-800 hover:underline">
                      + Add Account
                    </Link>
                  </div>
                  {isLoadingSavedAccounts ? (
                    <p className={`text-xs text-center py-4 ${muted}`}>Loading saved accounts...</p>
                  ) : matchingSavedAccounts.length > 0 && selectedSavedAccount ? (
                    <>
                      {matchingSavedAccounts.length > 1 ? (
                        <select
                          value={selectedSavedAccountId}
                          onChange={(e) => setSelectedSavedAccountId(e.target.value)}
                          className={`${inputCls} mb-3`}
                        >
                          {matchingSavedAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.accountTitle || account.walletTitle} · {maskAccountNumber(account.accountNumber)}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                        {[
                      { label: 'Bank Name', value: selectedSavedAccount.bankName },
                      { label: 'Account Title', value: selectedSavedAccount.accountTitle },
                      { label: 'Account Number', value: selectedSavedAccount.accountNumber },
                      { label: 'IBAN #', value: selectedSavedAccount.iban || selectedSavedAccount.swiftCode },
                        ].map((row) => (
                          <div key={row.label}>
                            <p className={`text-[10px] ${muted}`}>{row.label}</p>
                            <p className={`text-sm font-medium mt-0.5 ${text}`}>{displayVal(row.value)}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className={`rounded-xl border border-dashed p-5 text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-sm ${muted}`}>No saved account for this method.</p>
                      <Link to="/settings/withdrawal-details" className="inline-block mt-2 text-sm font-medium text-red-800 hover:underline">
                        Add withdrawal details
                      </Link>
                    </div>
                  )}
                </>
              ) : matchingSavedAccounts.length > 0 && selectedSavedAccount ? (
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  {[
                    { label: 'Account Title', value: selectedSavedAccount.accountTitle },
                    { label: 'Payment Method', value: selectedSavedAccount.walletTitle },
                    {
                      label: 'Wallet Address',
                      hint: 'TRC20 address will be used',
                      value: selectedSavedAccount.cryptoAddress,
                    },
                  ].map((row) => (
                    <div key={row.label}>
                      <p className={`text-[10px] ${muted}`}>{row.label}</p>
                      {row.hint ? (
                        <p className={`text-[10px] mt-0.5 ${muted}`}>{row.hint}</p>
                      ) : null}
                      <p className={`text-sm font-medium mt-0.5 break-all ${text}`}>
                        {row.label === 'Wallet Address' && row.value
                          ? shortenAddress(row.value)
                          : displayVal(row.value)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`rounded-xl border border-dashed p-5 text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-sm font-medium ${text}`}>No withdrawal details found</p>
                  <p className={`text-xs mt-1 mb-3 ${muted}`}>
                    Please add wallet details in Settings to continue.
                  </p>
                  <Link
                    to="/settings/withdrawal-details"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                    style={{ backgroundColor: THEME }}
                  >
                    + Add Wallet Details
                  </Link>
                </div>
              )}
            </div>

            {/* Step 3 */}
            <div className={`rounded-2xl border p-5 sm:p-6 shadow-sm ${card} ${!canProceedWithdrawal ? 'opacity-50' : ''}`}>
              <h2 className={`text-sm font-semibold mb-4 ${text}`}>Step 3: Confirm Withdrawal</h2>
              <div className={`rounded-xl p-4 mb-4 ${isDarkMode ? 'bg-red-950/30' : 'bg-red-50/60'}`}>
                {selectedSubPaymentMethod ? (
                  <div className="flex justify-between text-sm mb-2">
                    <span className={muted}>Payment Network</span>
                    <span className={text}>{selectedSubPaymentMethod.title}</span>
                  </div>
                ) : null}
                <div className="flex justify-between text-sm mb-2">
                  <span className={muted}>Withdrawal Amount</span>
                  <span className={text}>{formatCurrency(amountNum)}</span>
                </div>
                {isLocalBank ? (
                  <div className="flex justify-between text-sm mb-3">
                    <span className={muted}>Conversion Rate</span>
                    <span className={text}>{withdrawalRate}</span>
                  </div>
                ) : null}
                <div className="flex justify-between text-sm font-bold pt-3 border-t border-red-100">
                  <span className={text}>{isLocalBank ? 'Converted Amount' : 'You Will Receive'}</span>
                  <span style={{ color: THEME }}>{formatCurrency(receiveAmount)}</span>
                </div>
              </div>
              <div className={`flex gap-2 items-start text-xs mb-5 p-3 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-sky-50'}`}>
                <Info className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                <p className={muted}>Withdrawal requests are processed within 24 hours on business days.</p>
              </div>
              {!canProceedWithdrawal ? (
                <div
                  className={`text-sm rounded-xl px-4 py-3 mb-4 ${
                    isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Complete withdrawal details in Step 2 before confirming withdrawal.
                </div>
              ) : null}
              <button
                type="submit"
                disabled={isLoading || !canProceedWithdrawal}
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: THEME }}
              >
                {isLoading ? (
                  <>
                    <NewtonsCradleLoader size="24px" color="#ffffff" />
                    Processing...
                  </>
                ) : (
                  'Confirm Withdrawal'
                )}
              </button>
            </div>
          </form>

          {/* Right sidebar */}
          <div className="space-y-4">
            {/* Balance overview */}
            <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
              <h3 className={`text-sm font-semibold mb-4 ${text}`}>Balance Overview</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Available Balance', value: availableBalance, highlight: true },
                  { label: 'Pending Commission', value: transactionWidgetData?.pendingCommission ?? 0 },
                  { label: 'Paid Commission', value: transactionWidgetData?.paidCommission ?? 0 },
                  { label: 'Total Earned', value: transactionWidgetData?.totalEarned ?? 0 },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between">
                    <span className={muted}>{row.label}</span>
                    <span className={`font-semibold ${row.highlight ? 'text-red-800' : text}`} style={row.highlight ? { color: THEME } : {}}>
                      {isLoadingWidgets ? '...' : formatCurrency(row.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent payouts */}
            <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm font-semibold ${text}`}>Recent Payouts</h3>
                <Link to="/payout-history" className="text-xs font-medium text-red-800 hover:underline">View All</Link>
              </div>
              {isLoadingHistory ? (
                <p className={`text-xs text-center py-4 ${muted}`}>Loading...</p>
              ) : withdrawalHistory.length === 0 ? (
                <p className={`text-xs text-center py-4 ${muted}`}>No recent payouts</p>
              ) : (
                <div className="space-y-3">
                  {withdrawalHistory.slice(0, 4).map((payout) => (
                    <div key={payout.id} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <Building2 className="w-4 h-4" style={{ color: THEME }} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-xs font-medium truncate ${text}`}>{payout.method}</p>
                          <p className={`text-[10px] ${muted}`}>
                            {payout.date ? new Date(payout.date).toLocaleDateString() : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`text-xs font-semibold ${text}`}>{formatCurrency(payout.amount)}</p>
                        <StatusBadge status={payout.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default Withdrawal;
